import { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import TicketsScannerCheckedInLabel from './CheckedInLabel'
import TicketsScannerSearchAutocomplete from './SearchAutocomplete'
import TicketsScannerExtendedListModal from './ExtendedListModal'
import {
  setSearchURL,
  setFindTicketUrl,
  setCheckInUrl,
  setRemoveCheckInUrl,
  setupCounter,
  setupActionCableSubscription,
  updateQuery as updateQueryAction,
  forceSearch as forceSearchAction,
  selectOwners,
  selectOwnersError,
  selectShowExtendedModal,
  selectResetTrigger,
} from '../../stores/ticketsScanner/actions'

const TicketsScannerSearchInput = (props) => {
  const [autocompleteFocus, setAutocompleteFocus] = useState(false)
  const [query, setQuery] = useState("")
  const searchTimeout = useRef(null)
  const inputRef = useRef(null)
  const autocompleteRef = useRef(null)
  
  const owners = useSelector(selectOwners)
  const searchError = useSelector(selectOwnersError)
  const showExtendedModal = useSelector(selectShowExtendedModal)
  const resetTrigger = useSelector(selectResetTrigger)
  const dispatch = useDispatch()

  useEffect(() => {
    const {
      searchOwnerUrl,
      findTicketUrl,
      checkInUrl,
      removeCheckInUrl,
      checkedTickets,
      totalTickets,
    } = props

    // Initialize Redux store with URLs
    dispatch(setSearchURL(searchOwnerUrl))
    dispatch(setFindTicketUrl(findTicketUrl))
    dispatch(setCheckInUrl(checkInUrl))
    dispatch(setRemoveCheckInUrl(removeCheckInUrl))
    dispatch(setupCounter(checkedTickets, totalTickets))
    dispatch(setupActionCableSubscription())

    // Focus input
    if (inputRef.current) {
      inputRef.current.focus()
    }

    return () => {
      clearTimeout(searchTimeout.current)
    }
  }, [])

  useEffect(() => {
    // Handle reset trigger
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [resetTrigger])

  const handleFocus = () => {
    dispatch(updateQueryAction(""))
  }

  const handleChange = (e) => {
    clearTimeout(searchTimeout.current)
    setAutocompleteFocus(false)
    setQuery(e.target.value)
    const newQuery = e.target.value
    
    searchTimeout.current = setTimeout(() => {
      dispatch(updateQueryAction(newQuery))
    }, 100)
  }

  const handleKeyUp = (e) => {
    // Arrow up
    if (e.keyCode == "38" && autocompleteRef.current) {
      setAutocompleteFocus(true)
      autocompleteRef.current.scrollToPrev()
    }

    // Arrow down
    if (e.keyCode == "40" && autocompleteRef.current) {
      setAutocompleteFocus(true)
      autocompleteRef.current.scrollToNext()
    }

    // Enter
    if (e.keyCode == "13") {
      if (autocompleteFocus) {
        autocompleteRef.current.touchUnderScroll()
      } else if (!owners.loaded || owners.list.length > 0) {
        dispatch(forceSearchAction(e.target.value))
      }
    }
  }

  // `owners.loaded` is only ever set by a *successful* search, so a first failure would leave the
  // dropdown unmounted and the failure invisible.
  const isShowAutocomplete = () => {
    return !showExtendedModal && (owners.loaded || Boolean(searchError))
  }

  return (
    <div className="ibox float-e-margins">
      <div className="ibox-title">
        <h5>{I18n.t("tickets_scanner.input_title")}</h5>
        <TicketsScannerCheckedInLabel />
      </div>
      <div className="ibox-content">
        <input
          ref={inputRef}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyUp={handleKeyUp}
          value={query}
          type="text"
          placeholder={I18n.t("tickets_scanner.input_placeholder")}
          className="form-control"
          autoComplete="off"
        />

        {isShowAutocomplete() ? (
          <TicketsScannerSearchAutocomplete
            owners={owners}
            ref={autocompleteRef}
            onSelect={() => inputRef.current && inputRef.current.focus()}
          />
        ) : null}
      </div>

      <TicketsScannerExtendedListModal />
    </div>
  )
}

export default TicketsScannerSearchInput
